import { clamp, range } from '@library/utils'
import { isDefined } from 'common/helpers'
import type { Nullable } from 'common/types'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePrevious } from './usePrevious'
import { useUnmountEffect } from './useUnmountEffect'

type MouseEventHandler = (event: MouseEvent | ReactMouseEvent) => void
export type KeyboardEventHandler = (event: KeyboardEvent | ReactKeyboardEvent) => void

export type OrderedEventHandler = (order: number) => MouseEventHandler

export const useMultipleSelect = <T extends { id: string }>(objects: T[]) => {
  const length = objects.length

  const localObjects = usePrevious(objects)
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set())
  const [cursorIndex, setCursorIndex] = useState<Nullable<number>>(null)

  const handleEmptyClick: MouseEventHandler = event => {
    if (event.target === event.currentTarget) {
      setSelectedIndexes(new Set())
    }
  }

  const clamped = useCallback((value: number) => clamp(value, 0, length - 1), [length])

  const handleKeyDown = useCallback<KeyboardEventHandler>(
    event => {
      let newCursor = cursorIndex ?? -1
      switch (event.code) {
        case 'KeyA':
          {
            if (event.ctrlKey) {
              // выделить все
              event.preventDefault()
              setSelectedIndexes(new Set(range(length)))
            }
          }
          break
        case 'ArrowUp':
          {
            event.preventDefault()
            // сдвинуть курсор
            newCursor--
            if (event.ctrlKey && event.shiftKey) {
              // добавить выделение
              setSelectedIndexes(prev => new Set(prev.add(clamped(newCursor))))
            } else if (!event.ctrlKey) {
              // установить выделение
              setSelectedIndexes(new Set([clamped(newCursor)]))
            }
          }
          break
        case 'ArrowDown':
          {
            event.preventDefault()
            // сдвинуть курсор
            newCursor++
            if (event.ctrlKey && event.shiftKey) {
              // добавить выделение
              setSelectedIndexes(prev => new Set(prev.add(clamped(newCursor))))
            } else if (!event.ctrlKey) {
              // установить выделение
              setSelectedIndexes(new Set([clamped(newCursor)]))
            }
          }
          break
        case 'Space':
          {
            if (event.ctrlKey) {
              // переключить выделение на курсоре
              setSelectedIndexes(prev => new Set(toggleElementInSet(prev, newCursor)))
            }
          }
          break
        // снять все выделение
        case 'Escape': {
          setSelectedIndexes(new Set())
        }
      }

      setCursorIndex(clamped(newCursor))
    },
    [clamped, length, cursorIndex],
  )

  const handleItemClick = useCallback<OrderedEventHandler>(
    order => event => {
      const { ctrlKey: isAdditiveSelect, shiftKey: isRangeSelect } = event

      const isToggleOne = isAdditiveSelect && !isRangeSelect
      const isSelectRange = !isAdditiveSelect && isRangeSelect
      const isAddRange = isAdditiveSelect && isRangeSelect
      const isSelectOne = !isAdditiveSelect && !isRangeSelect

      // клик с shift и ctrl без предыдущего -- ничего, даже не обновлять предыдущий
      if (isAddRange && cursorIndex === null) {
        return
      }

      try {
        // клик без всего -- выбор кликнутого
        if (isSelectOne) {
          setSelectedIndexes(new Set([order]))
          return
        }

        // клик с ctrl -- переключение выделения для одного
        if (isToggleOne) {
          setSelectedIndexes(prev => new Set(toggleElementInSet(prev, order)))
          return
        }

        // клик с shift при наличии предыдущего -- выделение от предыдущего до кликнутого
        if (isSelectRange && cursorIndex !== null) {
          const [start, end] = minMax(order, cursorIndex)

          setSelectedIndexes(new Set(range(start, end + 1)))
          return
        }

        // клик с shift и ctrl при наличии предыдущего -- добавление выделения от minmax до кликнутого
        if (isAddRange && cursorIndex !== null) {
          const sorted = Array.from(selectedIndexes).sort((one, other) => one - other)
          const [min, max] = [sorted.at(0), sorted.at(-1)].filter(isDefined)
          const start = Math.min(min, max, order)
          const end = Math.max(min, max, order)

          setSelectedIndexes(new Set(range(start, end + 1)))
        }
      } finally {
        // Предыдущий обновляется только без шифта
        if (!isRangeSelect) {
          setCursorIndex(order)
        }
      }
    },
    [cursorIndex, selectedIndexes],
  )

  const indexes = useMemo(() => Array.from(selectedIndexes.values()), [selectedIndexes])

  useEffect(() => {
    if (!localObjects) return

    // Мы должны при удалении предшествующих cursorIndex и добавлении любых (добавляются в начало списка)
    // элементов пересчитывать cursorIndex
    const deletedIndex = localObjects.findIndex(
      item => !objects.some(object => object.id === item.id),
    )

    const isCursorExist = cursorIndex !== null
    const isAnyDeleted = deletedIndex > -1
    const updatedElementsCount = objects.length - localObjects.length
    const isAnyAdded = updatedElementsCount > 0
    const isDeletedBeforeCursor = isCursorExist && cursorIndex > deletedIndex

    if (isCursorExist && ((isAnyDeleted && isDeletedBeforeCursor) || isAnyAdded)) {
      setCursorIndex(cursorIndex + updatedElementsCount)
    }
  }, [objects, cursorIndex, localObjects])

  useUnmountEffect(() => {
    setCursorIndex(null)
  })

  return {
    cursorIndex,
    selectedIndexes: indexes,
    handleItemClick,
    handleKeyDown,
    handleEmptyClick,
    setSelectedIndexes,
    setCursorIndex,
  }
}

const minMax = (one: number, other: number) => (one < other ? [one, other] : [other, one])

const toggleElementInSet = (set: Set<number>, element: number) => {
  const wasInSet = set.delete(element)
  if (!wasInSet) {
    set.add(element)
  }

  return set
}
